import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Link2, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";
import { ficEiStatusLabel } from "../utils/fatturazioneSdi";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatEuro(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `€ ${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function RecuperoCollegamentoFicDialog({
  open,
  onOpenChange,
  headers,
  fattura,
  strutturaIdForConfig,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [data, setData] = useState(null);
  const [searchCf, setSearchCf] = useState("");
  const [searchNumero, setSearchNumero] = useState("");

  const configParams = useMemo(
    () => (strutturaIdForConfig ? { strutturaId: strutturaIdForConfig } : {}),
    [strutturaIdForConfig],
  );

  const loadCandidati = useCallback(
    async (overrides = {}) => {
      if (!fattura?.id) return;
      setLoading(true);
      try {
        const params = { ...configParams };
        const cfVal = overrides.cf ?? searchCf;
        const numVal = overrides.numero ?? searchNumero;
        if (cfVal.trim()) params.cf = cfVal.trim();
        if (numVal.trim()) {
          const n = parseInt(numVal, 10);
          if (Number.isFinite(n)) params.numero = n;
        }
        const res = await axios.get(
          `${API_URL}/api/fatturazione/fatture/${fattura.id}/cerca-corrispondenze-fic`,
          { headers, params },
        );
        setData(res.data);
      } catch (err) {
        setData(null);
        toast.error(err.response?.data?.detail || "Ricerca su FIC non riuscita");
      } finally {
        setLoading(false);
      }
    },
    [fattura?.id, headers, configParams, searchCf, searchNumero],
  );

  useEffect(() => {
    if (!open || !fattura) return;
    const cf = fattura.destinatario_cf || "";
    const num =
      fattura.numero_progressivo != null && fattura.numero_progressivo !== ""
        ? String(fattura.numero_progressivo)
        : "";
    setSearchCf(cf);
    setSearchNumero(num);
    setData(null);
    loadCandidati({ cf, numero: num });
  }, [open, fattura?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- load una sola volta all'apertura dialog

  const handleCollega = async (candidato) => {
    if (!fattura?.id || !candidato?.fic_document_id) return;
    if (!candidato.collegabile) {
      toast.error("Questo documento FIC è già collegato a un'altra fattura Affidya.");
      return;
    }
    const label = `FIC n. ${candidato.numero}`;
    if (
      !window.confirm(
        `Collegare la fattura Affidya ${fattura.numero} al documento ${label} su Fatture in Cloud?`,
      )
    ) {
      return;
    }
    setLinkingId(candidato.fic_document_id);
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/fatture/${fattura.id}/collega-documento-fic`,
        {
          fic_document_id: Number(candidato.fic_document_id),
          ...(strutturaIdForConfig ? { strutturaId: strutturaIdForConfig } : {}),
        },
        { headers },
      );
      toast.success(res.data?.message || "Fattura collegata a FIC");
      if (res.data?.avvisi?.length) {
        res.data.avvisi.forEach((a) => toast.warning(a));
      }
      onSuccess?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Collegamento non riuscito");
    } finally {
      setLinkingId(null);
    }
  };

  const candidati = data?.candidati || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-violet-600" />
            Recupero collegamento Fatture in Cloud
          </DialogTitle>
          <DialogDescription>
            Cerca su FIC il documento corrispondente a{" "}
            <strong>{fattura?.numero || "—"}</strong> e collegalo al record Affidya esistente
            (senza creare un duplicato).
          </DialogDescription>
        </DialogHeader>

        {fattura && (
          <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 text-sm space-y-1">
            <p>
              <span className="text-slate-500">Destinatario:</span>{" "}
              {fattura.destinatario_nome || "—"}
            </p>
            <p>
              <span className="text-slate-500">Totale Affidya:</span> {formatEuro(fattura.totale)}
              {fattura.numero_progressivo != null && (
                <>
                  {" · "}
                  <span className="text-slate-500">Progressivo:</span> {fattura.numero_progressivo}
                </>
              )}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Codice fiscale destinatario</Label>
            <Input
              value={searchCf}
              onChange={(e) => setSearchCf(e.target.value)}
              placeholder="CF destinatario"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Numero progressivo FIC</Label>
            <Input
              value={searchNumero}
              onChange={(e) => setSearchNumero(e.target.value)}
              placeholder="Es. 145"
              className="mt-1"
            />
          </div>
        </div>

        <Button type="button" variant="outline" onClick={() => loadCandidati()} disabled={loading}>
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Search className="w-4 h-4 mr-2" />
          )}
          Cerca corrispondenze su FIC
        </Button>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : candidati.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            Nessun candidato trovato. Verifica numero progressivo e CF, oppure controlla la tab
            Fatture attive.
          </p>
        ) : (
          <ul className="space-y-2">
            {candidati.map((c) => (
              <li
                key={c.fic_document_id}
                className={cn(
                  "flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm",
                  c.collegabile ? "border-slate-200" : "border-amber-200 bg-amber-50/50 opacity-80",
                )}
              >
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">FIC n. {c.numero}</span>
                    <span className="text-slate-500">{c.data}</span>
                    {c.score >= 70 && (
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-800">
                        <Sparkles className="w-3 h-3 mr-0.5 inline" />
                        Probabile match
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">
                    {c.destinatario} · CF {c.destinatario_cf || "—"} · {formatEuro(c.totale)}
                  </p>
                  {c.ei_status && (
                    <p className="text-xs text-slate-500">SDI: {ficEiStatusLabel(c.ei_status)}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Badge variant="outline" className="text-[10px]">
                      Score {c.score}
                    </Badge>
                    {(c.motivi || []).map((m) => (
                      <Badge key={m} className="text-[10px] bg-violet-100 text-violet-800">
                        {m}
                      </Badge>
                    ))}
                  </div>
                  {!c.collegabile && (
                    <p className="text-xs text-amber-700 mt-1">
                      Già collegato a un&apos;altra fattura Affidya.
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700"
                  disabled={!c.collegabile || linkingId === c.fic_document_id}
                  onClick={() => handleCollega(c)}
                >
                  {linkingId === c.fic_document_id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <>
                      <Link2 className="w-3.5 h-3.5 mr-1" />
                      Collega
                    </>
                  )}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
